// src/PrivateRoute.js
import React, { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { FaInfoCircle } from 'react-icons/fa';
import { UserContext } from './UserContext';

/**
 * True when the user's organisation has been assigned `menu`.
 * System admins, users without an organisation and sessions from before
 * menus existed (no `menus` array) are unrestricted.
 */
/** A user of an organisation who is not a system admin. */
export function isOrgUser(user) {
  return !!user && user.is_system_admin !== true && !!user.organisation_id;
}

export function hasMenu(user, menu) {
  if (!menu || !user) return true;
  if (user.is_system_admin === true || !user.organisation_id) return true;
  if (!Array.isArray(user.menus)) return true;
  return user.menus.includes(menu);
}

/**
 * Permission-based route guard.
 *
 *   <PrivateRoute>                       … just requires a logged-in user
 *   <PrivateRoute require="cases.read">  … logged-in AND has the permission
 *   <PrivateRoute requireAny={['users.read','roles.read']}>
 *                                        … logged-in AND has at least one
 *   <PrivateRoute menu="kyc">             … organisation has the KYC menu
 *
 * System admins bypass all permission checks.
 */
export default function PrivateRoute({
  children,
  require = null,
  requireAny = null,
  menu = null,
  staffOnly = false,   // not for organisation users (e.g. Manage Roles)
  redirectTo = '/Pips/pips'
}) {
  const { user } = useContext(UserContext);
  const token = localStorage.getItem('token');

  if (!user || !token) {
    return <Navigate to="/login" replace />;
  }

  const isSysAdmin = user.is_system_admin === true;
  const perms = Array.isArray(user.permissions) ? user.permissions : [];

  if (require && !isSysAdmin && !perms.includes(require)) {
    return <Navigate to={redirectTo} replace />;
  }

  if (Array.isArray(requireAny) && requireAny.length > 0 && !isSysAdmin) {
    const ok = requireAny.some(p => perms.includes(p));
    if (!ok) return <Navigate to={redirectTo} replace />;
  }

  if (staffOnly && isOrgUser(user)) {
    return <Navigate to={redirectTo} replace />;
  }

  // Not redirected: the fallback page may itself be a removed menu.
  if (!hasMenu(user, menu)) {
    return (
      <div className="page-container">
        <div className="alert-banner alert-banner--warning" style={{ marginTop: '2rem' }}>
          <span className="alert-banner__body">
            <FaInfoCircle />{' '}
            This page is not enabled for your organisation. Contact your
            system administrator to have it assigned.
          </span>
        </div>
      </div>
    );
  }

  return children;
}
